
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useResponsive } from '@/hooks/useResponsive';
import { useAlertsRealtime } from '@/hooks/useAlertsRealtime';
import { useAnalytics } from '@/hooks/useAnalytics';
import MobileHeader from './layout/MobileHeader';
import MobileSidebar from './layout/MobileSidebar';
import DesktopHeader from './layout/DesktopHeader';
import DesktopSidebar from './layout/DesktopSidebar';
import { supabase } from '@/integrations/supabase/client';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { isMobile, isTablet } = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  
  // Use the new real-time alerts hook
  const { alerts, markAlertAsRead } = useAlertsRealtime();
  
  // Initialize analytics tracking
  useAnalytics();

  // Accept any project invites sent to this user's email, even if they never opened the link.
  useEffect(() => {
    (supabase as any).rpc('claim_my_dev_invites').then(({ data }: any) => {
      if (data > 0) window.dispatchEvent(new Event('dev-invites-claimed'));
    });
  }, []);

  // Check if current route is a project detail page that needs more space
  const isProjectDetailPage = /^\/brrr-projects\/[^/]+$/.test(location.pathname) ||
                              /^\/development-projects\/[^/]+/.test(location.pathname);

  // Always start with sidebar closed on mobile/tablet, or on project detail pages
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    } else if (isProjectDetailPage) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile, isTablet, isProjectDetailPage]);

  // Close sidebar when route changes on mobile/tablet
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isTablet]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  const handleMobileMenuItemClick = () => {
    setSidebarOpen(false);
  };

  // Mobile Layout
  if (isMobile || isTablet) {
    return (
      <div className="min-h-screen w-full bg-background relative overflow-y-auto">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={handleOverlayClick}
          />
        )}

        <MobileHeader 
          onToggleSidebar={toggleSidebar}
          alerts={alerts}
          onNavigate={() => setSidebarOpen(false)}
          onMarkAsRead={markAlertAsRead}
        />

        <MobileSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onMenuItemClick={handleMobileMenuItemClick}
        />

        {/* Mobile Main Content */}
        <main className="pt-16 min-h-screen w-full">
          <div className="p-4 w-full">
            {children}
          </div>
        </main>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen w-full bg-background flex">
      {/* Desktop Sidebar */}
      <div className={`transition-all duration-300 ease-in-out ${
        sidebarOpen ? "w-64" : "w-0"
      } flex-shrink-0 overflow-hidden`}>
        <DesktopSidebar isOpen={sidebarOpen} />
      </div>
      
      {/* Desktop Main Content */}
      <main className="flex-1 overflow-x-hidden">
        <DesktopHeader onToggleSidebar={toggleSidebar} />
        <div className="container py-6 px-6 w-full max-w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
